import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { AdminGolfCourse, apiService } from '../../services/api';

interface Props {
  course: AdminGolfCourse;
  onSuccess: () => void;
  onCancel: () => void;
}

interface CourseUsageInfo {
  activeMonitoringRequests: number;
  totalHistoricalRequests: number;
  lastRequestDate?: string;
}

function DeleteCourseDialog({ course, onSuccess, onCancel }: Props) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageInfo, setUsageInfo] = useState<CourseUsageInfo | null>(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);

  useEffect(() => {
    fetchCourseUsage();
  }, [course.id]);

  const fetchCourseUsage = async () => {
    try {
      setIsLoadingUsage(true);
      // This would call a backend endpoint to get course usage statistics
      // For now, we'll simulate it since the exact endpoint might not exist
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock data - in real implementation, this would come from the backend
      setUsageInfo({
        activeMonitoringRequests: Math.floor(Math.random() * 5),
        totalHistoricalRequests: Math.floor(Math.random() * 50),
        lastRequestDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
      });
    } catch (error: any) {
      console.error('Failed to fetch course usage:', error);
      // Continue without usage info
      setUsageInfo({
        activeMonitoringRequests: 0,
        totalHistoricalRequests: 0
      });
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await apiService.deleteGolfCourse(course.id);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to delete golf course:', error);
      setError(error.message || 'Failed to delete golf course');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="text-red-600" size={20} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Delete Golf Course</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Course Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ExternalLink className="text-gray-600 mt-1" size={16} />
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{course.name}</h3>
                <p className="text-sm text-gray-600">{course.location}</p>
                <p className="text-sm text-gray-600">
                  API Type: <span className="font-medium">{course.api_type.toUpperCase()}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Status: <span className={`font-medium ${course.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {course.is_active ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Usage Information */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 mb-2">Impact Assessment</h4>
            
            {isLoadingUsage ? (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Loader2 className="animate-spin" size={14} />
                <span>Checking course usage...</span>
              </div>
            ) : usageInfo ? (
              <div className="space-y-2 text-sm text-yellow-700">
                <div className="flex justify-between">
                  <span>Active monitoring requests:</span>
                  <span className="font-medium">{usageInfo.activeMonitoringRequests}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total historical requests:</span>
                  <span className="font-medium">{usageInfo.totalHistoricalRequests}</span>
                </div>
                {usageInfo.lastRequestDate && (
                  <div className="flex justify-between">
                    <span>Last request:</span>
                    <span className="font-medium">{formatDate(usageInfo.lastRequestDate)}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-yellow-700">Unable to load usage information</p>
            )}
          </div>

          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">⚠️ This action cannot be undone</h4>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• The golf course will be permanently removed from the system</li>
              <li>• All active monitoring requests for this course will be cancelled</li>
              <li>• Historical data and statistics will be preserved for reporting</li>
              <li>• Users will no longer be able to select this course for monitoring</li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-600 mb-3">
              To confirm deletion, please type the course name exactly:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-2">
              "{course.name}"
            </p>
            <input
              type="text"
              placeholder="Type course name here"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              id="confirmationInput"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const confirmationInput = document.getElementById('confirmationInput') as HTMLInputElement;
              if (confirmationInput?.value === course.name) {
                handleDelete();
              } else {
                setError('Course name does not match. Please type the exact course name to confirm deletion.');
              }
            }}
            disabled={isDeleting}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <Trash2 size={16} />
            )}
            <span>{isDeleting ? 'Deleting...' : 'Delete Course'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteCourseDialog;